import { useEffect, useState } from 'react';
import type { MerchantRow, UiMerchantStatus } from '../../api/types';
import { useAuth } from '../../context/AuthContext';
import { mapDbToUi, statusLabel } from '../../utils/merchantStatus';
import { allowedQuickStatuses } from '../../utils/roles';
import { Modal } from '../ui/Modal';

type ChangeStatusModalProps = {
  merchant: MerchantRow | null;
  open: boolean;
  onClose: () => void;
  onSave: (merchantId: string, status: UiMerchantStatus) => Promise<void>;
};

export function ChangeStatusModal({ merchant, open, onClose, onSave }: ChangeStatusModalProps) {
  const { role } = useAuth();
  const options = allowedQuickStatuses(role);
  const [status, setStatus] = useState<UiMerchantStatus>('active');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (merchant && open) {
      setStatus(mapDbToUi(merchant.status));
      setError(null);
    }
  }, [merchant, open]);

  const submit = async () => {
    if (!merchant) return;
    setLoading(true);
    setError(null);
    try {
      await onSave(merchant.id, status);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Change merchant status"
      open={open && Boolean(merchant)}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-outline" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary btn-inline"
            disabled={loading}
            onClick={() => void submit()}
          >
            {loading ? 'Saving…' : 'Save status'}
          </button>
        </>
      }
    >
      {merchant ? (
        <>
          <p className="modal-muted">{merchant.name}</p>
          <label className="login-label" htmlFor="merchant-status">
            Status
          </label>
          <select
            id="merchant-status"
            className="login-input"
            value={status}
            onChange={(e) => setStatus(e.target.value as UiMerchantStatus)}
          >
            {options.map((opt) => (
              <option key={opt} value={opt}>
                {statusLabel(opt)}
              </option>
            ))}
          </select>
          {error ? <p className="form-error">{error}</p> : null}
        </>
      ) : null}
    </Modal>
  );
}
